import dayjs, { Dayjs } from 'dayjs'
import { computed, inject, provide, readonly, ref, nextTick } from 'vue'
import 'dayjs/locale/ja'
import { loadingController } from '@ionic/vue'
import { useCalendarPagesInMonthQuery } from '@/composable/apollo/graphql'
const weekday = require('dayjs/plugin/weekday')
dayjs.extend(weekday)
dayjs.locale('ja')

const getCalendarArray = (currentMoment: Dayjs) => {
  // この月に何日まであるかを算出
  const numOfMonth = currentMoment.endOf('month').date()
  // この月の1日〜最終日までの配列
  const daysOfMonth = [...Array(numOfMonth)].map((day, i) => ++i)
  // 1日の曜日（0~6の数値で取得）
  const firstWeekDay = currentMoment.startOf('month').weekday()
  // 週ごとの二次元配列を生成
  const data = [...Array(6)].map((empty, weekIndex) =>
    [...Array(7)].map((empty, dayIndex) => {
      const i = 7 * weekIndex + dayIndex - firstWeekDay
      if (i < 0 || daysOfMonth[i] === undefined) {
        return null
      }
      return daysOfMonth[i]
    })
  )
  // 全てnullの配列があれば除去する
  return data
    .map((week) => week.filter((day) => !!day) as number[])
    .filter((week) => week.length > 0)
}

const getCalendarData = (currentMoment: Dayjs) => {
  return {
    before: getCalendarArray(currentMoment.add(-1, 'month')),
    current: getCalendarArray(currentMoment),
    after: getCalendarArray(currentMoment.add(1, 'month')),
  }
}

// type CalendarData = ReturnType<typeof getCalendarData>

export const createCalendarBaseModule = () => {
  const [currentMonth, setCurrentMonth] = [
    ref<Dayjs>(dayjs().add(0, 'month').date(1)),
    async (year: number, month: number) => {
      const loadingContainer = await loadingController.create({})
      await loadingContainer.present()
      await reloadCalendarPages(dayjs().year(year).month(month).date(1))
      currentMonth.value = dayjs().year(year).month(month).date(1)
      await nextTick()
      loadingContainer.dismiss()
    },
  ]
  const { result, loading, refetch } = useCalendarPagesInMonthQuery({
    date: currentMonth.value.format('YYYY-MM-DD'),
  })

  const reloadCalendarPages = async (date: Dayjs) => {
    await refetch({
      date: date.format('YYYY-MM-DD'),
    })
  }

  const setCurrentMonthPrev = () => {
    setCurrentMonth(currentMonth.value.year(), currentMonth.value.month() - 1)
  }
  const isCurrentMonthToday = computed(() => {
    return dayjs().isSame(currentMonth.value, 'month')
  })
  const setCurrentMonthCurrent = () => {
    const today = dayjs().date(1)
    setCurrentMonth(today.year(), today.month())
  }
  const setCurrentMonthNext = () => {
    setCurrentMonth(currentMonth.value.year(), currentMonth.value.month() + 1)
  }

  const calendarDateList = computed(() => getCalendarData(currentMonth.value))

  const calendarPages = computed(() => {
    if (loading.value) return null
    return calendarDateList.value.current.map((row) =>
      row.map((col) => {
        const cp = result.value.calendarPagesInMonth?.find((calendarPage) => {
          return col === dayjs(calendarPage.date).date()
        })
        return {
          date: cp ? dayjs(cp.date) : currentMonth.value.date(col),
          totalPrice: cp?.totalPrice || 0,
        }
      })
    )
  })

  const getCalendarPageData = (date: Dayjs) => {
    const page = result.value.calendarPagesInMonth?.find((calendarPage) => {
      return dayjs(calendarPage.date).isSame(date, 'date')
    })
    return page
  }
  return {
    loading,
    isCurrentMonthToday,
    setCurrentMonth,
    setCurrentMonthPrev,
    setCurrentMonthCurrent,
    setCurrentMonthNext,
    currentMonth: readonly(currentMonth),
    getCalendarPageData,
    calendarDateList,
    calendarPages,
  }
}

type CalendarBaseModule = ReturnType<typeof createCalendarBaseModule>

export const key = Symbol('CalendarBaseModule')

export const provideCalendarBaseModule = () => {
  const store = createCalendarBaseModule()
  provide<CalendarBaseModule>(key, store)
  return store
}

export const useCalendarBaseModule = () => {
  const value = inject<CalendarBaseModule>(key)
  if (value === undefined) {
    throw new Error('CalendarBaseModule is not provided')
  }
  return value
}
